import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Stack } from '@mui/material';

import { CircularProgress, Typography } from '@occc/ui-design-system';
import { Page, useAppLayoutContextConsumer } from '@occc-app/design-system';

import { parseDate, formatDateTime, DateTimeFormat } from '@occc-app/lib/date';

import { useTobaccoApiOfferDetails } from './use-tobacco-api-offer-details.hook';
import { useTobaccoApiOfferLocations } from './use-tobacco-api-offer-locations.hook';
import { buildTobaccoApiOfferDetailsBreadcrumbs } from './breadcrumbs';
import { OfferEligibilityCard } from './components/offer-eligibility-card.component';
import { IncludedStoresTable } from './components/included-stores-table.component';

const PAGE_SUBTITLE = 'See Included Stores, Set External ID and Eligibility';

export const TobaccoApiOfferDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { offer, loading } = useTobaccoApiOfferDetails(id);
  const {
    locations,
    loading: locationsLoading,
    error: locationsError,
    paginationProps,
    search,
    onSearch,
  } = useTobaccoApiOfferLocations(id);

  const pageTitle = offer?.name ?? 'Offer Details';

  const breadcrumbs = useMemo(() => buildTobaccoApiOfferDetailsBreadcrumbs(pageTitle), [pageTitle]);
  useAppLayoutContextConsumer(breadcrumbs);

  if (loading && !offer) {
    return (
      <Page
        browserTabTitle='Offer Details'
        pageHeaderProps={{ title: 'Loading...', subtitle: '' }}
      >
        <CircularProgress />
      </Page>
    );
  }

  if (!offer) {
    return (
      <Page
        browserTabTitle='Offer Details'
        pageHeaderProps={{ title: 'Offer Not Found', subtitle: '' }}
      >
        <Typography variant='body1'>The requested offer could not be found.</Typography>
      </Page>
    );
  }

  const startDate = formatDateTime(parseDate(offer.startDate), DateTimeFormat.DateSlashed);
  const endDate = formatDateTime(parseDate(offer.endDate), DateTimeFormat.DateSlashed);
  const runDates = `${startDate} - ${endDate}`;

  return (
    <Page
      browserTabTitle={pageTitle}
      pageHeaderProps={{
        title: pageTitle,
        subtitle: PAGE_SUBTITLE,
      }}
    >
      <Stack spacing={1}>
        <OfferEligibilityCard offer={offer} runDates={runDates} />
        <IncludedStoresTable
          locations={locations}
          loading={locationsLoading}
          error={locationsError}
          paginationProps={paginationProps}
          search={search}
          onSearch={onSearch}
        />
      </Stack>
    </Page>
  );
};
